import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';

import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import Pagination from '@/components/common/Pagination';

import { getNotices, delNotice } from '@/apis/notices';
import type { NoticesItem } from '@/types/notices.types';
import type { ErrorResponse } from '@/types/client.types';

import { QUERY_KEY } from '@/constants/queryKey';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import NoticeFormModal from '@/components/admin/NoticeManage/NoticeFormModal';
import NoticeFilters from '@/components/admin/NoticeManage/NoticeFilters';
import NoticeListItem from '@/components/admin/NoticeManage/NoticeListItem';
import PageHelpModal from '@/components/common/PageHelpModal';
import PageHelpTrigger from '@/components/common/PageHelpTrigger';
import NoticeManageGuide from '@/content/guides/NoticeManageGuide';

import type { NoticeFilterType } from '@/types/notices.types';

import { Theme } from '@/styles/Theme';

const AdminNoticeManagePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? 1);
  const limit = 5;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<NoticesItem | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [filters, setFilters] = useState<NoticeFilterType>({});
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: [QUERY_KEY.ADMIN.NOTICE_LIST, page, filters],
    queryFn: () =>
      getNotices({
        platform: 'web',
        page,
        limit,
        ...filters,
      }),
  });

  const handleFilterChange = (newFilters: NoticeFilterType) => {
    setFilters(newFilters);
    setSearchParams({ page: '1' });
  };

  const handleFilterReset = () => {
    setFilters({});
    setSearchParams({ page: '1' });
  };

  const { mutate: deleteMutate } = useMutation({
    mutationFn: delNotice,
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEY.ADMIN.NOTICE_LIST]);
      queryClient.invalidateQueries([QUERY_KEY.UPDATE_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.NOTICE_POPUP]);
      queryClient.invalidateQueries([QUERY_KEY.LANDING_POPUP]);
      toast.success('공지사항이 삭제되었습니다.');
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data?.message ?? '삭제에 실패했습니다.');
    },
  });

  const handleDelete = (id: number) => {
    if (window.confirm('이 공지사항을 삭제하시겠습니까?')) {
      deleteMutate({ id: [id] });
    }
  };

  const noticesData = data?.data;
  const notices = noticesData?.formattedNotices ?? [];
  const totalCount = noticesData?.totalCount ?? 0;

  return (
    <Container>
      <HeaderRow>
        <TitleRow>
          <Text fontWeight={700} fontSize='1.5625rem' margin='0'>
            공지사항 관리
          </Text>
          <PageHelpTrigger
            onClick={() => setIsHelpOpen(true)}
            ariaLabel="공지사항 관리 사용 가이드"
          />
        </TitleRow>
        <Button
          backgroundColor={Theme.colors.B_3}
          width='8.75rem'
          height='2.5rem'
          onClick={() => setIsAddModalOpen(true)}
        >
          공지 추가
        </Button>
      </HeaderRow>

      <NoticeFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleFilterReset}
      />

      <ListContainer>
        {notices.length === 0 ? (
          <EmptyState>등록된 공지사항이 없습니다.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>제목</Th>
                <Th>플랫폼</Th>
                <Th>중요</Th>
                <Th>팝업</Th>
                <Th>수정일</Th>
                <Th>관리</Th>
              </tr>
            </thead>
            <tbody>
              {notices.map((notice) => (
                <NoticeListItem
                  key={notice.id}
                  notice={notice}
                  onEdit={() => setEditingNotice(notice)}
                  onDelete={() => handleDelete(notice.id)}
                  isExpanded={expandedId === notice.id}
                  onToggle={() =>
                    setExpandedId((id) => (id === notice.id ? null : notice.id))
                  }
                />
              ))}
            </tbody>
          </Table>
        )}
      </ListContainer>

      {totalCount > 0 && (
        <Pagination
          page={page}
          size={limit}
          totalCount={totalCount}
          showInfo={true}
        />
      )}

      {isAddModalOpen && (
        <NoticeFormModal
          setIsModalOpen={setIsAddModalOpen}
          onSuccess={() => {
            setIsAddModalOpen(false);
            queryClient.invalidateQueries([QUERY_KEY.ADMIN.NOTICE_LIST]);
            queryClient.invalidateQueries([QUERY_KEY.UPDATE_INFO]);
            queryClient.invalidateQueries([QUERY_KEY.NOTICE_POPUP]);
            queryClient.invalidateQueries([QUERY_KEY.LANDING_POPUP]);
          }}
        />
      )}

      {editingNotice && (
        <NoticeFormModal
          setIsModalOpen={(open) => !open && setEditingNotice(null)}
          editingNotice={editingNotice}
          onSuccess={() => {
            setEditingNotice(null);
            queryClient.invalidateQueries([QUERY_KEY.ADMIN.NOTICE_LIST]);
            queryClient.invalidateQueries([QUERY_KEY.UPDATE_INFO]);
            queryClient.invalidateQueries([QUERY_KEY.NOTICE_POPUP]);
            queryClient.invalidateQueries([QUERY_KEY.LANDING_POPUP]);
          }}
        />
      )}

      {isHelpOpen && (
        <PageHelpModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          title="공지사항 관리 사용 가이드"
        >
          <NoticeManageGuide />
        </PageHelpModal>
      )}
    </Container>
  );
};

export default AdminNoticeManagePage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
`;

const ListContainer = styled.div`
  background-color: ${Theme.colors.WHITE};
  border-radius: 0.5rem;
  box-shadow: 0 0 0.25rem rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  color: ${Theme.colors.G_2};
  font-size: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 1rem 1.25rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  background-color: ${Theme.colors.G_7};
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
`;
